'use server';

import { createClient, createAdminClient } from '@/lib/supabase/server';
import { redirect } from 'next/navigation';
import { headers } from 'next/headers';
import { mintKioskUnlockToken } from '@/lib/kioskUnlockToken';
import { isStaffRole } from '@/lib/roles';
import { verifyTurnstileToken } from '@/lib/turnstile';
import { escapeIlikeLiteral } from '@/lib/searchEscape';
import { friendlyAuthError } from '@/lib/authErrors';
import { clientRateLimitKey, consumeRateLimit, RATE_LIMIT_TOO_MANY } from '@/lib/rateLimit';

// Turnstile tokens are single-use - this is the ONLY place siteverify gets
// called for the login/reset forms. Supabase's own built-in CAPTCHA check
// (Auth > Attack Protection) must stay OFF, or it would try to re-verify the
// same already-consumed token against Cloudflare and fail every request.
export async function verifyCaptcha(token: string): Promise<{ success: boolean }> {
  const forwardedFor = (await headers()).get('x-forwarded-for');
  const remoteIp = forwardedFor?.split(',')[0]?.trim();
  const success = await verifyTurnstileToken(token, remoteIp);
  return { success };
}

export interface PinGateUser {
  id: string;
  vid: string;
  firstName: string;
  lastName: string;
  role: string;
}

export interface PinGateSuccess {
  user: PinGateUser;
  /** HMAC-signed proof that PinGate succeeded - required for kiosk writes. */
  unlockToken: string;
}

// Verifies VID + PIN via the verify_pin_gate() RPC (service-role client, bypassing
// RLS so the kiosk can authenticate before any Supabase Auth session exists). The
// RPC does a hashed compare and an atomic attempt-lockout - no plaintext PIN or
// attempt-counting logic lives in the app layer.
//
// On success also mints a short-lived unlockToken so later kiosk checkout/return
// actions can prove PinGate ran - a raw staff UUID alone is not enough.
export async function verifyPinGate(
  vid: string,
  pin: string,
  requiredRoles?: string[]
): Promise<PinGateSuccess | { error: string }> {
  const supabase = await createAdminClient();
  const ip = await clientRateLimitKey();
  if (!(await consumeRateLimit({ bucket: 'pin_gate', key: ip, limit: 8, windowSec: 15 * 60 }))) {
    return { error: RATE_LIMIT_TOO_MANY };
  }

  const { data: rpcData, error } = await supabase
    .rpc('verify_pin_gate', { p_vid: vid.trim(), p_pin: pin.trim() })
    .maybeSingle();

  const data = rpcData as {
    user_id: string; out_vid: string; first_name: string; last_name: string; role: string; error: string | null;
  } | null;

  if (error) return { error: error.message };
  if (!data || data.error) return { error: data?.error ?? 'PIN verification failed.' };
  if (requiredRoles && !isStaffRole(data.role)) {
    return { error: 'Access denied - staff only.' };
  }

  const user: PinGateUser = {
    id: data.user_id,
    vid: data.out_vid,
    firstName: data.first_name,
    lastName: data.last_name,
    role: data.role,
  };

  return {
    user,
    unlockToken: mintKioskUnlockToken({
      staffId: user.id,
      vid: user.vid,
      role: user.role,
      scope: 'checkout',
    }),
  };
}

/** Return-tab gate: staff VID + PIN (same RPC as checkout). Mints a return-scoped
 *  unlock token that can complete kiosk returns but cannot create checkouts. */
export async function verifyKioskStaffVidForReturn(
  vid: string,
  pin: string,
  requiredRoles?: string[]
): Promise<PinGateSuccess | { error: string }> {
  const supabase = await createAdminClient();
  const ip = await clientRateLimitKey();
  if (!(await consumeRateLimit({ bucket: 'pin_gate', key: ip, limit: 8, windowSec: 15 * 60 }))) {
    return { error: RATE_LIMIT_TOO_MANY };
  }

  const { data: rpcData, error } = await supabase
    .rpc('verify_pin_gate', { p_vid: vid.trim(), p_pin: pin.trim() })
    .maybeSingle();

  const data = rpcData as {
    user_id: string; out_vid: string; first_name: string; last_name: string; role: string; error: string | null;
  } | null;

  if (error) return { error: error.message };
  if (!data || data.error) return { error: data?.error ?? 'PIN verification failed.' };
  if (requiredRoles) {
    if (!isStaffRole(data.role)) {
      return { error: 'Access denied - staff only.' };
    }
  } else if (!isStaffRole(data.role)) {
    return { error: 'Only staff can process returns at the kiosk.' };
  }

  const user: PinGateUser = {
    id: data.user_id,
    vid: data.out_vid,
    firstName: data.first_name,
    lastName: data.last_name,
    role: data.role,
  };

  return {
    user,
    unlockToken: mintKioskUnlockToken({
      staffId: user.id,
      vid: user.vid,
      role: user.role,
      scope: 'return',
    }),
  };
}

// Looks up a student by VID for kiosk checkout. Uses admin client to bypass RLS
// (kiosk browser has no auth session, so direct client queries always return empty).
export async function lookupKioskStudent(
  vid: string
): Promise<{ id: string; vid: string; firstName: string; lastName: string } | { error: string }> {
  const supabase = await createAdminClient();

  const { data } = await supabase
    .from('profiles')
    .select('id, vid, first_name, last_name, role')
    .ilike('vid', escapeIlikeLiteral(vid.trim()))
    .maybeSingle();

  if (!data || !data.vid) return { error: 'Student not found - check the ID.' };
  if (data.role !== 'student') return { error: 'Only students can use the kiosk.' };

  return { id: data.id, vid: data.vid, firstName: data.first_name, lastName: data.last_name };
}

/**
 * Password sign-in via Server Action so session cookies are written with
 * `cookies().set()` on the action response - not via the browser client's
 * `document.cookie` path.
 *
 * Live symptom (2026-08-26): auth `/token` returned 200 dozens of times in a
 * minute for the same account while the UI bounced back to `/login`. Client
 * `signInWithPassword` + even `window.location.href` still raced cookie
 * commit vs `proxy.ts` `getUser()`, so middleware redirected unauthenticated
 * visitors straight back. Setting cookies on the Server Action response, then
 * hard-navigating only after that promise resolves, closes the race.
 *
 * Do not trim `password` - trailing spaces are part of the credential.
 */
export async function loginWithPassword(
  email: string,
  password: string,
  captchaToken?: string,
): Promise<{ success: true; role: string } | { success: false; error: string }> {
  const trimmedEmail = email.trim();
  if (!trimmedEmail || !password) {
    return { success: false, error: 'Please enter your email and password.' };
  }

  const ip = await clientRateLimitKey();
  const loginKey = `${ip}:${trimmedEmail.toLowerCase()}`;
  if (!(await consumeRateLimit({ bucket: 'login', key: loginKey, limit: 8, windowSec: 15 * 60 }))) {
    return { success: false, error: RATE_LIMIT_TOO_MANY };
  }

  const turnstileRequired = Boolean(process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY);
  if (turnstileRequired) {
    if (!captchaToken) {
      return { success: false, error: 'Complete the CAPTCHA challenge first.' };
    }
    const { success } = await verifyCaptcha(captchaToken);
    if (!success) {
      return { success: false, error: 'CAPTCHA verification failed. Please try again.' };
    }
  }

  const supabase = await createClient();
  const { data, error } = await supabase.auth.signInWithPassword({
    email: trimmedEmail,
    password,
  });
  if (error) {
    return { success: false, error: friendlyAuthError(error.message) };
  }
  if (!data.user) {
    return { success: false, error: 'Something went wrong signing in. Please try again.' };
  }

  const { data: profile, error: profileError } = await supabase
    .from('profiles')
    .select('role')
    .eq('id', data.user.id)
    .single();

  if (profileError || !profile?.role) {
    await supabase.auth.signOut();
    return {
      success: false,
      error: 'Signed in, but no profile is set up for this account yet. Contact a staff member.',
    };
  }

  return { success: true, role: profile.role };
}

export async function logout() {
  const supabase = await createClient();
  await supabase.auth.signOut();
  redirect('/login');
}
